import Launch from "@mui/icons-material/Launch";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Link from "@mui/joy/Link";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { PersonQuartzClientContext } from "../../../shared/services/api/contexts";
import { PersonId } from "../../../models/person";
import { Participant, ParticipantId } from "../../../models/study";
import { completionColor } from "../../../shared/utils/fact";
import { formatAndCapitalizeString } from "../../../shared/utils/text";
import UnifiedDataTable, { DataTableColumn } from "../../../shared/components/ui/UnifiedDataTable";

interface VerificationCompletion {
  [key: PersonId]: number;
}

interface ParticipantsTableProps {
  readonly participants: Participant[];
  readonly selected: ParticipantId[];
  readonly setSelected: React.Dispatch<React.SetStateAction<ParticipantId[]>>;
  readonly onAddParticipant: () => void;
  readonly onCSVImport: () => void;
}

export default function ParticipantsTable({
  participants,
  selected,
  setSelected,
  onAddParticipant,
  onCSVImport,
}: ParticipantsTableProps) {
  const personQuartzClient = React.useContext(PersonQuartzClientContext);
  const [filtered, setFiltered] = React.useState<Participant[]>(participants);
  const [verificationCompletion, setVerificationCompletion] =
    React.useState<VerificationCompletion>({});

  // Helper function to get the latest state for a participant
  const getParticipantCurrentState = (participant: Participant) => {
    return participant.progress.reduce((max, current) => {
      return new Date(max.start_date) > new Date(current.start_date) ? max : current;
    }, participant.progress[0]);
  };

  // Update the filtered list when the "participants" prop changes.
  React.useEffect(() => {
    setFiltered(participants);
  }, [participants]);

  React.useEffect(() => {
    const controller = new AbortController();

    async function getVerificationCompletion() {
      try {
        const verificationCompletion =
          await personQuartzClient.getPersonVerificationCompletion(controller);
        setVerificationCompletion(verificationCompletion);
      } catch (error: any) {
        if (error.name === "AbortError") {
          return;
        }
        console.error(error);
      }
    }

    getVerificationCompletion();

    return () => controller.abort();
  }, [personQuartzClient, participants]);

  const columns: DataTableColumn<Participant>[] = [
    {
      key: "participant_details",
      header: "Link to participant details",
      width: "320px",
      render: (participant) => (
        <Link
          underline="hover"
          href={"/patient/" + participant.person_id}
          target="_blank"
          rel="noopener"
          endDecorator={<Launch fontSize="small" />}
        >
          {participant.person_id.length > 10
            ? `${participant.person_id.substring(0, 10)}...`
            : participant.person_id}
        </Link>
      ),
    },
    {
      key: "identifier",
      header: "Identifier",
      render: (participant) => (
        <Typography>
          {participant.identifier.length > 0 ? participant.identifier[0].value : ""}
        </Typography>
      ),
    },
    {
      key: "current_status",
      header: "Current status",
      render: (participant) => (
        <Typography>
          {formatAndCapitalizeString(getParticipantCurrentState(participant).state)}
        </Typography>
      ),
    },
    {
      key: "data_review",
      header: "Data review",
      render: (participant) => {
        if (verificationCompletion[participant.person_id] != null) {
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress
                determinate
                value={verificationCompletion[participant.person_id]}
                color={completionColor(verificationCompletion[participant.person_id])}
                sx={{ "--CircularProgress-size": "30px" }}
              />
              <Typography>{verificationCompletion[participant.person_id]}%</Typography>
            </Stack>
          );
        }
        return <Typography level="body-xs">No note</Typography>;
      },
    },
  ];

  const emptyState = {
    icon: <PersonOffIcon sx={{ fontSize: 128 }} color="disabled" />,
    title: "No participant in this study yet.",
    description:
      'You can link a cohort to this study to automatically add participants in "Overview" or add participants manually.',
    actions: (
      <Stack direction="row" spacing={1}>
        <Button variant="solid" onClick={onAddParticipant}>
          Add Participant
        </Button>
        <Button variant="solid" onClick={onCSVImport}>
          Import CSV
        </Button>
      </Stack>
    ),
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormControl sx={{ width: 240 }}>
          <FormLabel id="select-field-participant-status" htmlFor="select-field-participant-status">
            Participant status
          </FormLabel>
          <Select
            id="select-field-participant-status"
            defaultValue="all"
            onChange={(e, newValue) => {
              if (newValue === "all") {
                setFiltered(participants);
              } else {
                setFiltered(
                  participants.filter(
                    (participant) => getParticipantCurrentState(participant).state === newValue
                  )
                );
              }
            }}
          >
            <Option value="all">All</Option>
            {Array.from(
              new Set(
                participants.map((participant) => getParticipantCurrentState(participant).state)
              )
            ).map((state) => (
              <Option key={state} value={state}>
                {formatAndCapitalizeString(state)}
              </Option>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1}>
          <Button variant="solid" onClick={onAddParticipant}>
            Add Participant
          </Button>
          <Button variant="solid" onClick={onCSVImport}>
            Import CSV
          </Button>
        </Stack>
      </Stack>

      <div style={{ marginTop: "16px" }}>
        <UnifiedDataTable
          data={filtered}
          columns={columns}
          getItemId={(participant) => participant.participant_id}
          selectable={true}
          selectedItems={selected}
          onSelectionChange={(newSelected) => setSelected(newSelected as ParticipantId[])}
          pageSize={50}
          paginationThreshold={50}
          ariaLabel="study participants table"
          emptyState={emptyState}
          height="auto"
        />
      </div>
    </>
  );
}
